import React, { useState, useEffect } from 'react';
import { Anchor, Button, Group, Modal, Text, Box, Alert, Space } from '@mantine/core';
import { IconDownload, IconAlertCircle } from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import { collection, query, getDocs, DocumentData, collectionGroup, where, getFirestore } from 'firebase/firestore';
import { Account, Customer, KYC } from '../types';

interface GenerateBurganExcelModalProps {
    opened: boolean;
    setOpened: (opened: boolean) => void;
}

const GenerateBurganExcelModal: React.FC<GenerateBurganExcelModalProps> = (props) => {
    const opened = props.opened;
    const setOpened = props.setOpened;
    
    const db = getFirestore();
    const [loading, setLoading] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState<string>('');
    const [fileName, setFileName] = useState<string>('');
    const [msg, setMsg] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const getAllIbans = (accounts: Account[], kycIban?: string): string => {
        // Collect all unique IBANs
        const ibans = new Set<string>();

        // Add IBANs from accounts
        accounts.forEach(acc => {
            if (acc.iban) {
                ibans.add(acc.iban);
            }
        });

        // Add KYC IBAN if exists
        if (kycIban) {
            ibans.add(kycIban);
        }

        // Join with semicolon
        return Array.from(ibans).join(';');
    };

    const exportToExcel = async () => {
        setLoading(true);
        setMsg(null);
        setDownloadUrl('');

        try {
            // 1: Fetch all customers
            const customersQuery = query(collection(db, "customers"));
            const customersSnapshot = await getDocs(customersQuery);
            
            const customerMap = new Map<string, Customer>();
            
            customersSnapshot.docs.forEach((document) => {
                const fullCustomerData = document.data();
                const customerData: Customer = {
                    id: document.id,
                    civilID: fullCustomerData.civilID,
                    crmID: fullCustomerData.crmID,
                    email: fullCustomerData.email,
                    paciEmail: fullCustomerData.paciEmail,
                    paciMobile: fullCustomerData.paciMobile,
                    mobile: fullCustomerData.mobile || '',
                    name_en: fullCustomerData.name_en || '',
                    name_ar: fullCustomerData.name_ar || '',
                    gender: fullCustomerData.gender,
                    nationality: fullCustomerData.nationality,
                    lastValidKYC: fullCustomerData.lastValidKYC,
                    address_from_finder: fullCustomerData.address_from_finder,
                    customerType: fullCustomerData.customerType
                };
                customerMap.set(document.id, customerData);
            });

            console.log(`Loaded ${customerMap.size} customers`);

            // 2: Fetch all valid KYCs using collectionGroup
            const kycQuery = query(
                collectionGroup(db, 'kyc'),
                where('state.status_code', '==', 'valid')
            );
            const kycSnapshot = await getDocs(kycQuery);
            
            // Group KYCs by customer ID and keep only the latest one
            const kycByCustomer = new Map<string, KYC>();
            
            kycSnapshot.docs.forEach((kycDoc) => {
                const customerId = kycDoc.ref.parent.parent?.id || '';

                if (!customerId) {
                    console.warn(`KYC ${kycDoc.id} has no parent customer ID`);
                    return; // Skip KYCs without a valid customer ID
                }
                
                const fullKycData = kycDoc.data();
                const kycData: KYC = {
                    id: kycDoc.id,
                    address: fullKycData.address || {},
                    name_en: fullKycData.name_en || '',
                    name_ar: fullKycData.name_ar || '',
                    civilID: fullKycData.civilID,
                    civilIDExpiryDate: fullKycData.civilIDExpiryDate,
                    nationality: fullKycData.nationality,
                    birthDate: fullKycData.birthDate,
                    gender: fullKycData.gender,
                    address_from_finder: fullKycData.address_from_finder,
                    employment_employerPhone: fullKycData.employment?.employerPhone || fullKycData.employment_employerPhone,
                    mobile: fullKycData.mobile || '',
                    iban: fullKycData.iban,
                    created: fullKycData.created // Add created field for comparison
                };
                
                // Keep only the latest KYC for each customer
                const existingKyc = kycByCustomer.get(customerId);
                if (!existingKyc || (kycData.created && existingKyc.created && kycData.created > existingKyc.created)) {
                    kycByCustomer.set(customerId, kycData);
                }
            });

            console.log(`Loaded ${kycByCustomer.size} valid KYCs`);

            // 3. Fetch all accounts using collection group
            const accountsQuery = query(collectionGroup(db, 'accounts'));
            const accountsSnapshot = await getDocs(accountsQuery);
            
            // Group accounts by customer ID
            const accountsByCustomer = new Map<string, Account[]>();
            
            accountsSnapshot.docs.forEach((accountDoc) => {
                const customerId = accountDoc.ref.parent.parent?.id || '';

                if (!customerId) {
                    console.warn(`Account ${accountDoc.id} has no parent customer ID`);
                    return; // Skip accounts without a valid customer ID
                }
                
                const data = accountDoc.data();
                const accountData: Account = {
                    iban: data.iban || ''
                };
                
                if (!accountsByCustomer.has(customerId)) {
                    accountsByCustomer.set(customerId, []);
                }
                accountsByCustomer.get(customerId)!.push(accountData);
            });

            console.log(`Loaded accounts for ${accountsByCustomer.size} customers`);

            // 4. Combine all data
            const dataList: DocumentData[] = [];
            
            customerMap.forEach((customer, customerId) => {
                const kyc = kycByCustomer.get(customerId) || null;
                const accounts = accountsByCustomer.get(customerId) || [];
                
                dataList.push({
                    customerObj: customer,
                    kycObj: kyc,
                    accounts: accounts
                });
            });

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();

            // Define all headers (64 columns total)
            const headers = [
                "creation date", "Client Type", "Client Number", "Client Name (En)",
                "Client Name (Ar)", "Gender(F/M)", "Nationality", "ID Number",
                "ID Expire Date", "Birth date ", "PLACE OF BIRTH ", "Email",
                "Phone", "Fax", "Mobile", "Mode of Correspondence",
                "Educational level", "work", "Employer", "employment address",
                "employment Tell No.", "Job title", "Years of service", "total assets",
                "total liabilities", "net assets", "Net profit", "Approximate Wealth",
                "annual income", "net income", "Account Holder's Name", "Bank",
                "Bank Account no", "2nd Bank Name", " 2nd Bank Account in KYC",
                "monthly income sources", "Approximate annual income",
                "Additional income sources", "Approximate net worth excluding the house",
                "General investment objectives", "Preferred Investment Category",
                "Omnibus Account ", "Date of Update", "SMS date", "Investment Experience ",
                "INVESTMENT AWARENESS  ", "RISK TOLERANCE ",
                "INVESTMENT OBJECTIVES & STRATEGY ", "country ", "P.O. Box ",
                "source of income ", "information investment & indicators ",
                "client preferential product ", "House No.", "Block", "Zip code",
                "Additional No", "City", "Street", "Bank Account Currency",
                "Distribution Number", "Registration No", "Registration Expiry Date", "Address"
            ];

            // Map customer data to Excel format
            const excelData = dataList.map(item => {
                const customer = item.customerObj as Customer;
                const kyc = item.kycObj as KYC;
                const accounts = item.accounts as Account[] || [];
                const row: any[] = new Array(64).fill('');

                // Fill only the yellow columns with data
                row[2] = customer.crmID || customer.id || ''; // Client Number
                row[3] = customer.name_en || kyc?.name_en || ''; // Client Name (En) - prefer customer
                row[4] = customer.name_ar || kyc?.name_ar || ''; // Client Name (Ar) - prefer customer

                // Gender - prefer customer data, fallback to KYC data
                row[5] = customer.gender || kyc?.gender || '';

                // Nationality - prefer customer data, fallback to KYC data
                row[6] = customer.nationality || kyc?.nationality || '';

                // ID Number - prefer civilID from customer
                row[7] = customer.civilID || kyc?.civilID || '';

                // ID Expire Date - from KYC or lastValidKYC
                const idExpireDate = customer.lastValidKYC?.civilIDExpiryDate || kyc?.civilIDExpiryDate;
                row[8] = idExpireDate ? idExpireDate.toDate().toLocaleDateString('en-GB') : '';

                // Birth date - from KYC
                row[9] = kyc?.birthDate ? kyc.birthDate.toDate().toLocaleDateString('en-GB') : '';

                // Email - prefer customer email
                row[11] = customer.email || customer.paciEmail || '';

                // Phone - from KYC employment data
                row[12] = kyc?.employment_employerPhone || '';

                // Mobile - prefer customer mobile
                row[14] = customer.mobile || customer.paciMobile || kyc?.mobile || '';

                // Bank Account no - concatenate all IBANs with semicolon
                row[32] = getAllIbans(accounts, kyc?.iban);

                // Address - construct from KYC or customer address_from_finder
                let address = '';
                if (kyc?.address_from_finder?.DetailsEnglish) {
                    const addr = kyc.address_from_finder;
                    address = addr.DetailsEnglish;
                } else if (customer.address_from_finder?.DetailsEnglish) {
                    const addr = customer.address_from_finder;
                    address = addr.DetailsEnglish;

                } else if (kyc?.address) {

                    let englishAddress = '';
                    if (kyc.address.Area && kyc.address.Area.trim() !== '') {
                        englishAddress = englishAddress + ', Area: ' + kyc.address.Area;
                    }
                    if (kyc.address.BlockNumber && kyc.address.BlockNumber.trim() !== '') {
                        englishAddress = englishAddress + ', Block Number: ' + kyc.address.BlockNumber;
                    }
                    if (kyc.address.StreetName && kyc.address.StreetName.trim() !== '') {
                        englishAddress = englishAddress + ', Street Name: ' + kyc.address.StreetName;
                    }
                    if (kyc.address.UnitNumber && kyc.address.UnitNumber.trim() !== '') {
                        englishAddress = englishAddress + ', Unit Number: ' + kyc.address.UnitNumber;
                    }
                    if (kyc.address.FloorNumber && kyc.address.FloorNumber.trim() !== '') {
                        englishAddress = englishAddress + ', Floor Number: ' + kyc.address.FloorNumber;
                    }
                    address = englishAddress;
                }
                row[63] = address;

                return row;
            });

            // Create worksheet with headers and data
            const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);

            // Yellow column indices
            const yellowIndices = [2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 14, 32, 63];

            // Apply column widths
            const colWidths = headers.map((header, index) => {
                if (yellowIndices.includes(index)) {
                    return { wch: Math.max(header.length + 2, 15) };
                }
                return { wch: Math.max(header.length + 2, 12) };
            });
            ws['!cols'] = colWidths;

            // Add autofilter
            ws['!autofilter'] = { ref: `A1:BL${excelData.length + 1}` };

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

            // Generate Excel file and download
            const tempFileName = `Burgan_Customers_${new Date().toISOString().split('T')[0]}.xlsx`;
            setFileName(tempFileName);

            // Convert workbook to blob
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            // Create download URL
            const url = URL.createObjectURL(blob);
            setDownloadUrl(url);

            setMsg({ type: 'success', message: `Successfully exported ${dataList.length} customers` });

        } catch (error: any) {
            console.error('Error exporting customers:', error);
            setMsg({ type: 'error', message: error.message || 'Error exporting customers. Please check the console for details.' });
        } finally {
            setLoading(false);
        }
    };

    // Cleanup function to revoke the URL when component unmounts
    useEffect(() => {
        return () => {
            if (downloadUrl) {
                URL.revokeObjectURL(downloadUrl);
            }
        };
    }, [downloadUrl]);

    return (
        <>
            <Modal
                opened={opened}
                onClose={() => {
                    setMsg(null);
                    setOpened(false);
                    setDownloadUrl('');
                    setFileName('');
                }}
                title="Generate Burgan Excel"
            >
                <Group>
                    <Button 
                        color="grape" 
                        type="submit" 
                        loading={loading}
                        leftSection={<IconDownload size={16} />}
                        onClick={exportToExcel}
                    >
                        Generate Excel
                    </Button>
                </Group>

                <Space h="xl" />

                {msg && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        color={msg.type === "success" ? "green" : "red"}
                        title={msg.type === "success" ? "Success" : "Failure"}
                    >
                        {msg.message}
                        <br />
                        {downloadUrl && (

                                <Anchor
                                    href={downloadUrl}
                                    download={fileName}
                                    pt={5}
                                >
                                    Download
                                </Anchor>
                        )}
                    </Alert>
                )}
            </Modal>
        </>
    );
};

export default GenerateBurganExcelModal;